import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import SecurityPicker, { PickedSecurity } from './shared/SecurityPicker';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  DockTitle,
  EconomicsPreview,
  InstrumentGrid,
  ScopePill,
  SearchDock,
  StructuredBand,
  StructuredContextPanel,
  StructuredHeroPanel,
  StructuredWorkspace,
  TradeEconomicsGrid,
  type StructuredContextRow,
} from './shared/StructuredCreditTicketLayout';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import { BadgeDollarSign, CalendarDays, FileText, Gauge, Landmark, Search, ShieldCheck, Users } from 'lucide-react';

import { DateField } from '../shared/DateField';

const fmtAmount = (value: string) => {
  const parsed = parseAmountShorthand(value);
  return parsed > 0 ? formatAmountWithCommas(parsed) : value;
};
const num = (value: any, fallback = 0) => {
  const n = value === '' || value == null ? fallback : parseAmountShorthand(String(value));
  return Number.isFinite(n) ? n : fallback;
};

const rowsFrom = (rows: Array<[string, any, boolean?]>): StructuredContextRow[] =>
  rows
    .filter(([, value]) => value != null && value !== '')
    .map(([label, value, accent]) => ({ label, value: String(value), accent }));

// ADR 0009: an MSR package trades and settles on SIFMA (its currency's calendar outside dollars),
// on the trade date (T+0). The server has the last word on the settle date.
const TICKET_TYPE = 'MSR';
const SETTLE_CONVENTION = 'T+0';

const settleQuery = (tradeDate: string, currency?: string) =>
  ({ tradeDate, ticketType: TICKET_TYPE, convention: SETTLE_CONVENTION, currency: currency || 'USD' });

/** The provisional settle date at once, then the server's, unless the form has moved on meanwhile. */
const syncSettleDate = (setFormData: React.Dispatch<React.SetStateAction<any>>, tradeDate: string, currency?: string) => {
  const query = settleQuery(tradeDate, currency);
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  void resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === tradeDate && (prev.currency || 'USD') === query.currency && prev.settleDate === provisional
      ? { ...prev, settleDate } : prev));
};

/** Runs once when a new ticket opens. */
const OnOpen: React.FC<{ run: () => void }> = ({ run }) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);
  return null;
};

// Read-only package context; also feeds default UPB + suggested price into the form.
const MsrPackageContext: React.FC<{ securityId?: number | null; onLoaded?: (d: any) => void }> = ({ securityId, onLoaded }) => {
  const [detail, setDetail] = useState<any | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!securityId) { setDetail(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`/api/credit/msr/securities/${securityId}/detail`);
        if (!cancelled && data?.found) { setDetail(data); onLoaded?.(data); }
        else if (!cancelled) setDetail(null);
      } catch { if (!cancelled) setDetail(null); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityId]);
  const rows = detail ? rowsFrom([
    ['Package', detail.msrName || detail.securityName, true],
    ['Servicer', detail.servicerName, true],
    ['Agency', detail.agency],
    ['Product', detail.productType],
    ['Serviced UPB', detail.currentUpb != null ? Number(detail.currentUpb).toLocaleString() : null, true],
    ['Loans', detail.numLoans],
    ['WAC', detail.wacPct != null ? `${detail.wacPct}%` : null],
    ['Fee', detail.servicingFeeBps != null ? `${detail.servicingFeeBps} bps` : null],
    ['Acq Multiple', detail.acquisitionMultiple],
  ]) : [];

  return (
    <StructuredContextPanel
      toneName="msr"
      icon={Gauge}
      title="Package Snapshot"
      subtitle={detail?.msrName || detail?.servicerName || 'Mortgage servicing economics'}
      rows={rows}
      emptyTitle={securityId ? 'Package detail unavailable' : 'No MSR package selected'}
      emptyText={securityId ? 'The selected security has no MSR package snapshot.' : 'Choose an MSR package to show servicer, agency, UPB, WAC, fee, and multiple.'}
    />
  );
};

const MSRTicketScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // A trade opened from the blotter keeps its stored settle date.
  const openingTrade = !!((location.state as any)?.transactionId || (location.state as any)?.id);
  const openTradeDate = getTodayDate(calendarsFor(TICKET_TYPE, 'USD'));
  const initialFormData = {
    transactionType: 'BUY' as const,
    assetManager: '', portfolioManager: '',
    assetClass: 'RESIDENTIAL_MSR', assetSubClass: 'MSR',
    portfolio: '', tradeDate: openTradeDate, settleDate: provisionalSettleDate(settleQuery(openTradeDate)),
    submittedBy: getCurrentOperatorName(), currency: 'USD', executedBy: '', notes: '',
    securityId: '' as any, cusip: '', security: '',
    upb: '', price: '',
  };

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    const isCreate = action === 'draft' || action === 'submitForPreClear';
    if (isCreate) {
      if (!formData.securityId) errors.securityId = 'Select an MSR package (or define one in Reference Data)';
      if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
      if (num(formData.upb) <= 0) errors.upb = 'Serviced UPB must be > 0';
      if (num(formData.price) <= 0) errors.price = 'Price (% of UPB) must be > 0';
      if (!formData.tradeDate) errors.tradeDate = 'Trade date is required';
    }
    if (action === 'execute' && !formData.executedBy?.trim()) errors.executedBy = 'Executed By is required';
    return errors;
  };

  const loadTransactionData = (transaction: any) => {
    const str = (v: any) => (v == null ? '' : v.toString());
    return {
      ...initialFormData,
      transactionType: transaction.transactionType || 'BUY',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      securityId: transaction.securityId || '',
      cusip: transaction.cusip || '',
      security: transaction.securityName || transaction.cusip || '',
      upb: str(transaction.amount != null ? transaction.amount : transaction.quantity),
      price: transaction.price != null ? transaction.price.toString() : '',
      tradeDate: transaction.tradeDate || getTodayDate(),
      settleDate: transaction.settleDate || getTodayDate(),
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
    };
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);
    if (!formData.securityId) throw new Error('No MSR package selected');
    let assetManagerId: number | null = null;
    if (formData.assetManager?.trim()) {
      const am = dimensionData.assetManagers.find((a: any) => a.assetManagerName === formData.assetManager.trim());
      if (am?.id) assetManagerId = am.id;
    }
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager?.trim()) {
      const pm = dimensionData.portfolioManagers.find((p: any) => p.portfolioManagerName === formData.portfolioManager.trim());
      if (pm?.id) portfolioManagerId = pm.id;
    }
    const upb = num(formData.upb);
    const price = num(formData.price, 1);
    return {
      transactionType: 'BUY',
      securityId: Number(formData.securityId),
      portfolioId: portfolio.id,
      assetManagerId, portfolioManagerId,
      quantity: upb, price, amount: upb,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate || formData.tradeDate,
      notes: formData.notes || `MSR acquisition: ${formData.security || formData.cusip}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'MSR',
      eventType: 'ACQUIRE',
      sourceRef: `msr:security-${formData.securityId}:acquire`,
      currency: formData.currency || 'USD',
      cusip: formData.cusip,
    };
  };

  return (
    <BaseTicketScreen
      title="MSR Trade Entry"
      assetClass="RESIDENTIAL_MSR"
      assetSubClass="MSR"
      hidePortfolioField
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange }) => {
        const pickerLocked = isFieldDisabled('securityId') || (!!transactionStatus && transactionStatus !== 'DRAFT');
        const selected: PickedSecurity | null = formData.securityId
          ? { id: Number(formData.securityId), cusip: formData.cusip, securityName: formData.security }
          : null;
        const upb = num(formData.upb);
        const price = num(formData.price, 1);
        const consideration = upb * price / 100;

        return (
          <StructuredWorkspace>
            {!openingTrade && <OnOpen run={() => syncSettleDate(setFormData, formData.tradeDate, formData.currency)} />}
            <StructuredHeroPanel
              toneName="msr"
              icon={FileText}
              eyebrow="Structured Credit"
              title="MSR Package"
              description="Mortgage servicing-right acquisition with servicer, WAC, servicing-fee strip, acquisition multiple, and UPB economics in one ticket."
              badges={['MSR', 'Serviced UPB', 'Price as % UPB']}
              signals={[
                { label: 'Selected', value: formData.security || 'No package', accent: !!formData.security },
                { label: 'CUSIP', value: formData.cusip || '-' },
                { label: 'Trade Unit', value: 'Serviced UPB' },
                { label: 'Currency', value: formData.currency || 'USD' },
              ]}
            />

            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="msr"><Search size={14} /> Instrument</BandEyebrow>
                  <BandTitle>Package Selection</BandTitle>
                  <BandSubline>Reference-data package with read-only servicing economics.</BandSubline>
                </BandTitleGroup>
                <ScopePill $tone="msr">Servicing Rights</ScopePill>
              </BandHeader>
              <InstrumentGrid>
                <SearchDock $tone="msr">
                  <DockTitle><ShieldCheck size={16} /> Approved Package</DockTitle>
                  <SecurityPicker
                    assetClass="RESIDENTIAL_MSR"
                    label="MSR Package (CUSIP)*"
                    placeholder="Search by package name or CUSIP..."
                    selected={selected}
                    disabled={pickerLocked}
                    error={errors.securityId}
                    notFoundLabel="Define new MSR package"
                    onDefineNew={() => navigate('/reference-data?tab=structured&assetClass=MSR&new=1')}
                    onSelect={(sec) => {
                      setFormData((prev: any) => ({
                        ...prev,
                        securityId: sec.id,
                        cusip: sec.cusip || '',
                        security: sec.securityName || sec.cusip || '',
                        currency: sec.currency || prev.currency,
                      }));
                      // Another currency settles on another calendar.
                      if (sec.currency && sec.currency !== formData.currency) syncSettleDate(setFormData, formData.tradeDate, sec.currency);
                    }}
                    onClear={() => setFormData((prev: any) => ({ ...prev, securityId: '', cusip: '', security: '' }))}
                  />
                </SearchDock>
                <MsrPackageContext
                  securityId={formData.securityId ? Number(formData.securityId) : null}
                  onLoaded={(d) => setFormData((prev: any) => {
                    const next = { ...prev };
                    if ((!prev.upb || prev.upb === '') && d.currentUpb != null) next.upb = fmtAmount(String(d.currentUpb));
                    if ((!prev.price || prev.price === '') && d.acquisitionMultiple != null && d.servicingFeeBps != null) {
                      next.price = (Number(d.acquisitionMultiple) * Number(d.servicingFeeBps) / 100).toFixed(4);
                    }
                    return next;
                  })}
                />
              </InstrumentGrid>
            </StructuredBand>

            <TradeEconomicsGrid>
              <StructuredBand>
                <BandHeader>
                  <BandTitleGroup>
                    <BandEyebrow $tone="msr"><CalendarDays size={14} /> Trade Setup</BandEyebrow>
                    <BandTitle>Portfolio, Dates, UPB, And Price</BandTitle>
                    <BandSubline>Acquisition ticket with serviced-UPB quantity and price expressed as percent of UPB.</BandSubline>
                  </BandTitleGroup>
                  <ScopePill $tone="msr">BUY</ScopePill>
                </BandHeader>

                <FormRow>
                  <FormGroup>
                    <Label>Portfolio*</Label>
                    <Select value={formData.portfolio} onChange={(e) => handleInputChange('portfolio', e.target.value)} className={errors.portfolio ? 'error' : ''} disabled={isFieldDisabled('portfolio')}>
                      <option value="">Select Portfolio</option>
                      {dimensionData.portfolios.map((p: any) => (<option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>))}
                    </Select>
                    {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label><Landmark size={13} /> Asset Manager</Label>
                    <Select value={formData.assetManager} onChange={(e) => handleInputChange('assetManager', e.target.value)} disabled={isFieldDisabled('assetManager')}>
                      <option value="">Select</option>
                      {dimensionData.assetManagers.map((am: any) => (<option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label><Users size={13} /> Portfolio Manager</Label>
                    <Select value={formData.portfolioManager} onChange={(e) => handleInputChange('portfolioManager', e.target.value)} disabled={isFieldDisabled('portfolioManager')}>
                      <option value="">Select</option>
                      {dimensionData.portfolioManagers.map((pm: any) => (<option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>))}
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Trade Date*</Label>
                    <DateField value={formData.tradeDate ?? ''} className={errors.tradeDate ? 'error' : ''} disabled={isFieldDisabled('tradeDate')} calendars={calendarsFor(TICKET_TYPE, formData.currency)} onChange={iso => { handleInputChange('tradeDate', iso); syncSettleDate(setFormData, iso, formData.currency); }} />
                    {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Settle Date</Label>
                    <DateField value={formData.settleDate ?? ''} disabled={isFieldDisabled('settleDate')} calendars={settlementCalendars({ ticketType: TICKET_TYPE, currency: formData.currency })} onChange={iso => handleInputChange('settleDate', iso)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                    <Select value={formData.executedBy} onChange={(e) => handleInputChange('executedBy', e.target.value)} disabled={isFieldDisabled('executedBy')} className={errors.executedBy ? 'error' : ''}>
                      <option value="">Select</option>
                      {dimensionData.users.map((u: any) => (<option key={u.id} value={u.fullName}>{u.fullName}{u.role ? ` (${u.role})` : ''}</option>))}
                    </Select>
                    {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label><BadgeDollarSign size={13} /> Serviced UPB*</Label>
                    <Input value={formData.upb} onChange={(e) => handleInputChange('upb', e.target.value)} onBlur={(e) => setFormData((p: any) => ({ ...p, upb: fmtAmount(e.target.value) }))} className={errors.upb ? 'error' : ''} disabled={isFieldDisabled('upb')} />
                    {errors.upb && <FieldError>{errors.upb}</FieldError>}
                  </FormGroup>
                  <FormGroup>
                    <Label>Price (% of UPB)*</Label>
                    <Input type="number" step="0.0001" value={formData.price} onChange={(e) => handleInputChange('price', e.target.value)} className={errors.price ? 'error' : ''} disabled={isFieldDisabled('price')} />
                    {errors.price && <FieldError>{errors.price}</FieldError>}
                  </FormGroup>
                </FormRow>
              </StructuredBand>

              <EconomicsPreview
                toneName="msr"
                title="Live Economics"
                notionalLabel="Serviced UPB"
                notionalValue={upb}
                price={price}
                consideration={consideration}
                currency={formData.currency || 'USD'}
                extraMetrics={[
                  { label: 'Settlement', value: formData.settleDate || '-' },
                  { label: 'Price Unit', value: '% of UPB', accent: true },
                ]}
              />
            </TradeEconomicsGrid>
          </StructuredWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default MSRTicketScreen;
